import { formatInlineRate } from '@packages/format';
import { UNIT_TYPE_REGISTRY, isUnitType, unitLabelForCount } from './activity-types.js';
import { formatDuration } from './time-increment.js';
/**
 * Muted sub-line string shown below the invoice line-item description (both the
 * PDF renderer and the Bill UI detail preview use this), or null when no
 * sub-line should render.
 *
 * Format for new-style rows (unitType set, non-zero qty and rate):
 *   "<qty> <label> @ R<rate>/<shorthand>"
 *     hours -> "2 hr 30 min @ R1,000/hr"   (formatDuration humanises sub-hour values)
 *     km    -> "60 km @ R8/km"
 *     pages -> "45 pages @ R3.00/page"     (plural-aware via unitLabelForCount)
 *     appearances -> "3 appearances @ R2,500/appearance"
 *
 * Lump-sum rows and historic rows (unitType === null) carry no qty/rate sub-line
 * because the amount column already shows the total and historic descriptions
 * already have the bracketed "(qty @ rate)" suffix baked in by
 * `buildLineItemDescription`. For those rows the sub-line degrades to the
 * performer name only when the account has more than one member, else null.
 *
 * Performer prefix is gated on `accountMemberCount > 1 && performedByName !== null`.
 * Single-member accounts render no performer anywhere. Callers pass the count of
 * FEE EARNERS (`isFeeEarnerRole`), not of seats: a bookkeeper can never be the
 * performer of a fee line, so counting one would start naming performers on a
 * sole practitioner's documents.
 */
export function formatInvoiceLineItemSubLine(item, accountMemberCount) {
    const includePerformer = accountMemberCount > 1 && item.performedByName !== null;
    // Validate unitType against the registry at render time. An unrecognised
    // value (direct SQL edit, out-of-spec import, forward-compat bug mid-deploy)
    // would otherwise crash this helper on the UNIT_TYPE_REGISTRY lookup below.
    // Degrading to the historic-row branch means the PDF still renders - the
    // sub-line is simply suppressed for that row.
    const hasStructuredQtyRate = item.unitType != null &&
        isUnitType(item.unitType) &&
        item.unitType !== 'lump_sum' &&
        item.units !== null &&
        item.pricePerUnitMinorUnits !== null &&
        item.units > 0 &&
        item.pricePerUnitMinorUnits > 0;
    if (!hasStructuredQtyRate) {
        return includePerformer ? item.performedByName : null;
    }
    const unitType = item.unitType;
    const labelShorthand = UNIT_TYPE_REGISTRY[unitType].labelShorthand;
    const rate = formatInlineRate(item.pricePerUnitMinorUnits, labelShorthand);
    const units = item.units;
    const qtyPhrase = unitType === 'hours' ? formatDuration(units) : `${units} ${unitLabelForCount(unitType, units)}`;
    const rateString = `${qtyPhrase} @ ${rate}`;
    if (includePerformer) {
        return `${item.performedByName} · ${rateString}`;
    }
    return rateString;
}
/**
 * The date to display for an invoice line item. Prefers the line item's own
 * `date`, falling back to its first linked activity's date. Manually-added line
 * items have no linked activity, so an activity-only lookup would leave their
 * date blank even though the date is set; activity-backed line items carry a
 * snapshot of the activity date in `date`, so they resolve via the first branch
 * and stay consistent with the issued PDF. Returns null when neither is set.
 *
 * Callers own their own formatting (the read-only invoice view formats the full
 * date; the draft editor slices to a short date) - this only resolves the value.
 */
export function resolveInvoiceLineItemDate(item) {
    return item.date ?? item.activities[0]?.date ?? null;
}
/**
 * Whether any item in the given list resolves a date via
 * {@link resolveInvoiceLineItemDate} (own date or activity fallback).
 *
 * Callers decide invoice-wide (over the visible, discount-filtered rows) once
 * this returns true or false, so a screen renders a Date column for every row
 * or none - never a mix of populated and blank cells across the same table.
 */
export function anyLineItemHasResolvableDate(items) {
    return items.some((item) => resolveInvoiceLineItemDate(item) !== null);
}
